import { useState, useEffect, useCallback } from 'react';
import { usePortalStore } from '@/store/portal-store.js';
import { makePortalInstance, type Artefact } from '@edison/api-client-react';
import { ExternalLink, FileText, Search, X, GitCommit, FileVideo, ScrollText, Lightbulb, Code2 } from 'lucide-react';
import { SkeletonRow } from '@/components/ui/skeleton.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { FilterChipGroup } from '@/components/ui/filter-chip.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { PortalMarkdown } from '@/components/ui/portal-markdown.js';

// Portal /evidence endpoint returns plaintext content; the generated Artefact type omits it (ERA-021).
type PortalArtefact = Artefact & { githubUrl?: string; content?: string | null; projectId?: string | null; sourceDate?: string | null };

const TYPE_LABEL: Record<string, string> = {
  github_commit: 'GitHub Commit',
  github_pr: 'GitHub PR',
  github_issue: 'GitHub Issue',
  meeting_transcript: 'Meeting Transcript',
  prd_document: 'PRD Document',
  llm_session: 'LLM Session',
  manual_note: 'Manual Note',
  abandoned_work: 'Abandoned Work',
  market_search: 'Market Search',
  file_upload: 'File Upload',
};

// Code-like types rendered monospace; prose types rendered as readable paragraphs.
const PROSE_TYPES = new Set(['meeting_transcript', 'prd_document', 'manual_note', 'llm_session', 'abandoned_work', 'market_search']);

function typeIcon(type: string) {
  switch (type) {
    case 'github_commit': return <GitCommit className="h-3.5 w-3.5" aria-hidden="true" />;
    case 'github_pr':
    case 'github_issue': return <Code2 className="h-3.5 w-3.5" aria-hidden="true" />;
    case 'meeting_transcript': return <FileVideo className="h-3.5 w-3.5" aria-hidden="true" />;
    case 'prd_document': return <ScrollText className="h-3.5 w-3.5" aria-hidden="true" />;
    case 'manual_note': return <Lightbulb className="h-3.5 w-3.5" aria-hidden="true" />;
    default: return <FileText className="h-3.5 w-3.5" aria-hidden="true" />;
  }
}

const TYPE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'github_commit', label: 'Commits' },
  { key: 'meeting_transcript', label: 'Transcripts' },
  { key: 'prd_document', label: 'Documents' },
];

// ── Portal-themed evidence detail panel ───────────────────────────────────────

function EvidencePanel({ item, onClose }: { item: PortalArtefact; onClose: () => void }) {
  const isProse = PROSE_TYPES.has(item.type);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" aria-hidden="true" onClick={onClose} />
      <aside
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[600px] flex-col bg-gray-950 border-l border-gray-800 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={item.title ?? 'Evidence detail'}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-800 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="inline-flex items-center gap-1.5 rounded bg-gray-800 px-2 py-0.5 text-xs text-gray-400">
                {typeIcon(item.type)}
                {TYPE_LABEL[item.type] ?? item.type}
              </span>
              <span className="text-xs text-gray-600">
                {new Date(item.sourceDate ?? item.createdAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            </div>
            <h2 className="text-sm font-semibold text-white leading-snug">{item.title || '—'}</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close panel"
            className="shrink-0 rounded-lg p-1.5 text-gray-500 hover:text-gray-200 hover:bg-gray-800 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* External link */}
        {item.githubUrl && (
          <div className="px-5 py-2.5 border-b border-gray-800 shrink-0">
            <a
              href={item.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              View on GitHub
            </a>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {item.content ? (
            isProse ? (
              <PortalMarkdown content={item.content} />
            ) : (
              <pre className="text-xs text-gray-300 font-mono leading-relaxed whitespace-pre-wrap break-words">
                {item.content}
              </pre>
            )
          ) : (
            <p className="text-sm text-gray-500 italic">No stored content for this artefact.</p>
          )}
        </div>
      </aside>
    </>
  );
}

// ── Main evidence page ─────────────────────────────────────────────────────────

interface EvidencePagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface EvidenceFacets {
  projects: Array<{ id: string; name: string }>;
  activities?: Array<{ id: string; name: string; projectId: string }>;
}

interface EvidenceResponse {
  data: {
    items: PortalArtefact[];
    pagination: EvidencePagination;
    facets?: EvidenceFacets;
  };
}

const PAGE_LIMIT = 50;

export function PortalEvidencePage() {
  const token = usePortalStore((s) => s.token);
  const mode = usePortalStore((s) => s.mode);
  const [items, setItems] = useState<PortalArtefact[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');
  const [facetProjects, setFacetProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [facetActivities, setFacetActivities] = useState<Array<{ id: string; name: string; projectId: string }>>([]);
  const [selected, setSelected] = useState<PortalArtefact | null>(null);

  // Debounce free-text search so we don't fire a request on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Server-side filtering + on-demand pagination — the backend now scopes by project/type/search
  // and returns only the requested page, so the client no longer needs to walk every page (ERA-022).
  const fetchPage = useCallback((pageNum: number, replace: boolean) => {
    if (!token) { setIsLoading(false); return; }
    const portal = makePortalInstance(token, { mode });
    const params = new URLSearchParams({ page: String(pageNum), limit: String(PAGE_LIMIT) });
    if (projectFilter !== 'all') params.set('projectId', projectFilter);
    if (activityFilter !== 'all') params.set('activityId', activityFilter);
    if (typeFilter !== 'all') params.set('type', typeFilter);
    if (debouncedSearch.trim()) params.set('q', debouncedSearch.trim());

    setError('');
    return portal.get<EvidenceResponse>(`/api/portal/evidence?${params.toString()}`)
      .then((res) => {
        const body = res.data;
        setItems((prev) => (replace ? body.items : [...prev, ...body.items]));
        setPage(body.pagination.page);
        setTotal(body.pagination.total);
        setFacetProjects(body.facets?.projects ?? []);
        setFacetActivities(body.facets?.activities ?? []);
      })
      .catch((err: { response?: { status?: number } }) => {
        setError(err?.response?.status === 403
          ? 'Underlying evidence is available to auditor-role access only. Your access covers the claim summary, expenditure breakdown and eligibility.'
          : 'Failed to load evidence.');
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadingMore(false);
      });
  }, [token, mode, projectFilter, activityFilter, typeFilter, debouncedSearch]);

  useEffect(() => {
    if (!token) { setIsLoading(false); return; }
    setIsLoading(true);
    void fetchPage(1, true);
    // fetchPage is intentionally omitted from deps — it's derived from the same filter values
    // already listed here, and including it would re-run this effect on every render.
  }, [token, mode, projectFilter, activityFilter, typeFilter, debouncedSearch]);

  const handleLoadMore = () => {
    setIsLoadingMore(true);
    void fetchPage(page + 1, false);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-7 w-48 rounded bg-gray-800 animate-pulse" />
        <div className="divide-y divide-gray-800">
          {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} className="px-4" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return <InlineError message={error} onRetry={() => fetchPage(1, true)} />;
  }

  const projectActivities = projectFilter === 'all'
    ? []
    : facetActivities.filter((a) => a.projectId === projectFilter);
  const selectClass = 'rounded-lg bg-gray-800 border border-gray-700 px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed max-w-[260px]';

  return (
    <>
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-white">Evidence</h1>
          <p className="text-sm text-gray-400 mt-1">
            Artefacts collected for this R&D claim
            {total > 0 ? ` — showing ${items.length} of ${total} artefacts.` : '.'}
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 pointer-events-none" aria-hidden="true" />
            <input
              type="search"
              placeholder="Search by title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg bg-gray-800 border border-gray-700 pl-9 pr-3 py-1.5 text-sm text-gray-200 placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              aria-label="Search evidence"
            />
          </div>
          <FilterChipGroup chips={TYPE_FILTERS} value={typeFilter} onChange={setTypeFilter} />
        </div>

        {/* Project → activity cascade: choosing a project scopes the activity dropdown to that
            project's activities; changing project resets the activity selection. */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={projectFilter}
            onChange={(e) => { setProjectFilter(e.target.value); setActivityFilter('all'); }}
            className={selectClass}
            aria-label="Filter by project"
          >
            <option value="all">All projects</option>
            {facetProjects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
            className={selectClass}
            disabled={projectFilter === 'all' || projectActivities.length === 0}
            aria-label="Filter by activity"
            title={projectFilter === 'all' ? 'Choose a project first' : undefined}
          >
            <option value="all">{projectFilter === 'all' ? 'All activities (choose a project first)' : 'All activities'}</option>
            {projectActivities.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>

        {items.length === 0 ? (
          <EmptyState
            icon={<FileText className="h-8 w-8" />}
            title="No evidence found"
            body={typeFilter === 'all' ? 'No evidence has been linked to this claim.' : `No ${typeFilter.replace('_', ' ')} evidence linked.`}
          />
        ) : (
          <>
            <div className="divide-y divide-gray-800 rounded-lg border border-gray-800 overflow-hidden">
              {items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setSelected(item)}
                  className="w-full bg-gray-900 hover:bg-gray-800/80 transition-colors px-4 py-3 text-left"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 text-xs rounded bg-gray-800 text-gray-400 px-2 py-0.5 shrink-0">
                          {typeIcon(item.type)}
                          {TYPE_LABEL[item.type] ?? item.type}
                        </span>
                        {item.githubUrl && (
                          <span className="inline-flex items-center gap-1 text-xs text-blue-400">
                            <ExternalLink className="h-3 w-3" aria-hidden="true" />
                            GitHub
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-200 truncate">{item.title || '—'}</p>
                      {item.content && (
                        <p className="text-xs text-gray-500 truncate">
                          {item.content.slice(0, 120).replace(/\n/g, ' ')}
                        </p>
                      )}
                    </div>
                    <p className="shrink-0 text-xs text-gray-600">
                      {new Date(item.sourceDate ?? item.createdAt).toLocaleDateString('en-AU')}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            {items.length < total && (
              <div className="flex justify-center">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="rounded-lg border border-gray-700 bg-gray-800 px-4 py-1.5 text-xs font-medium text-gray-300 hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {selected && <EvidencePanel item={selected} onClose={() => setSelected(null)} />}
    </>
  );
}
